import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// DS-07: 4 variantes sem borda, apenas tint de fundo + texto contrastado.
// Substitui banners ad-hoc (ex: CafAlertBanner). Per handoff design 2026-04.
const alert = cva(
  'flex items-start gap-3 rounded-md px-[1.1rem] py-[0.9rem] text-[0.83rem] leading-[1.5]',
  {
    variants: {
      tone: {
        warn: 'bg-[rgba(217,119,6,0.09)] text-[var(--alert-warn-fg)]',
        ok: 'bg-[rgba(61,143,95,0.09)] text-[var(--alert-ok-fg)]',
        danger: 'bg-[rgba(192,57,43,0.09)] text-[var(--alert-danger-fg)]',
        info: 'bg-[rgba(59,130,246,0.09)] text-[var(--alert-info-fg)]',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

const TONE_ICON: Record<NonNullable<VariantProps<typeof alert>['tone']>, LucideIcon> = {
  warn: AlertTriangle,
  ok: CheckCircle2,
  danger: AlertCircle,
  info: Info,
};

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alert> {
  title?: string;
  icon?: LucideIcon;
  children?: ReactNode;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { tone = 'info', title, icon, className, children, ...rest },
  ref,
) {
  const Icon = icon ?? TONE_ICON[tone ?? 'info'];
  return (
    <div ref={ref} role="alert" className={cn(alert({ tone }), className)} {...rest}>
      <Icon size={16} strokeWidth={1.75} className="shrink-0 mt-px" />
      <div className="min-w-0">
        {title && <strong className="font-semibold">{title}</strong>}
        {title && children ? ' — ' : null}
        {children}
      </div>
    </div>
  );
});
